<script>
import StateSelector from '../../components/StateSelector.vue';

export default {
  name: 'FarmAddressAutofill',

  components: {
    StateSelector,
  },

  props: {
    address: {
      type: String,
      default: '',
    },
    city: {
      type: String,
      default: '',
    },
    state: {
      type: String,
      default: '',
    },
    county: {
      type: [String, Number],
      default: '',
    },
    zipcode: {
      type: String,
      default: '',
    },
    locked: {
      type: Boolean,
      default: false,
    },
    mapboxToken: {
      type: String,
      required: true,
    },
    alerts: {
      type: Object,
      default: null,
    },
    editableClasses: {
      type: String,
      default: '',
    },
    editableClassesSelect: {
      type: String,
      default: '',
    },
  },

  emits: ['update:address', 'update:city', 'update:state', 'update:county', 'update:zipcode'],

  data() {
    return {
      addressSuggestions: [],
      addressSearchTimer: null,
      addressSearchLoading: false,
      addressSelectionInProgress: false,
      activeSuggestionIndex: -1,
      pendingCountyName: '',
    };
  },

  computed: {
    showAddressSuggestions() {
      return !this.locked && this.addressSuggestions.length > 0;
    },

    addressValue: {
      get() {
        return this.address || '';
      },
      set(value) {
        this.$emit('update:address', value);

        if (!this.addressSelectionInProgress) {
          this.queueAddressSearch(value);
        }
      },
    },

    cityValue: {
      get() {
        return this.city || '';
      },
      set(value) {
        this.$emit('update:city', value);
      },
    },

    stateValue: {
      get() {
        return this.state || '';
      },
      set(value) {
        this.$emit('update:state', this.normalizeStateCode(value));
      },
    },

    zipcodeValue: {
      get() {
        return this.zipcode || '';
      },
      set(value) {
        this.$emit('update:zipcode', value);
      },
    },
  },

  mounted() {
    window.addEventListener('click', this.handleWindowClick);
  },

  beforeUnmount() {
    window.removeEventListener('click', this.handleWindowClick);

    if (this.addressSearchTimer) {
      clearTimeout(this.addressSearchTimer);
    }
  },

  methods: {
    errorFor(field) {
      if (!this.alerts || typeof this.alerts.getError !== 'function') {
        return '';
      }

      return this.alerts.getError(field);
    },

    queueAddressSearch(value) {
      if (this.addressSearchTimer) {
        clearTimeout(this.addressSearchTimer);
      }

      const query = (value || '').trim();
      const numericOnly = /^\d+$/.test(query);

      if (!query || !this.mapboxToken) {
        this.clearAddressSuggestions();
        return;
      }

      if ((!numericOnly && query.length < 3) || (numericOnly && query.length < 2)) {
        this.clearAddressSuggestions();
        return;
      }

      this.addressSearchTimer = setTimeout(() => {
        this.fetchAddressSuggestions(query);
      }, 250);
    },

    async fetchAddressSuggestions(query) {
      this.addressSearchLoading = true;
      this.activeSuggestionIndex = -1;

      try {
        const trimmed = (query || '').trim();
        const numericOnly = /^\d+$/.test(trimmed);

        const types = numericOnly ? 'address,street' : 'address';

        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(trimmed)}.json?autocomplete=true&country=us&types=${types}&limit=6&access_token=${encodeURIComponent(this.mapboxToken)}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Mapbox request failed with status ${response.status}`);
        }

        const payload = await response.json();

        const features = Array.isArray(payload.features) ? payload.features : [];

        // Prefer address results over street results if both are present.
        const addressResults = features.filter(feature =>
            Array.isArray(feature.place_type) && feature.place_type.includes('address')
        );

        const streetResults = features.filter(feature =>
            Array.isArray(feature.place_type) && feature.place_type.includes('street')
        );

        this.addressSuggestions = (addressResults.length > 0 ? addressResults : streetResults).slice(0, 6);
      } catch (error) {
        console.error('Failed to fetch address suggestions:', error);
        this.addressSuggestions = [];
      } finally {
        this.addressSearchLoading = false;
      }
    },

    selectAddressSuggestion(feature) {
      this.addressSelectionInProgress = true;

      const parsed = this.parseMapboxFeature(feature);

      this.$emit('update:address', parsed.address);
      this.$emit('update:city', parsed.city);
      this.$emit('update:state', parsed.state);
      this.$emit('update:county', '');
      this.$emit('update:zipcode', parsed.zipcode);

      this.pendingCountyName = parsed.county || '';

      this.clearAddressSuggestions();

      this.$nextTick(() => {
        this.addressSelectionInProgress = false;
      });
    },

    parseMapboxFeature(feature) {
      const context = Array.isArray(feature.context) ? feature.context : [];
      const placeNameParts = typeof feature.place_name === 'string'
          ? feature.place_name.split(',').map(part => part.trim())
          : [];

      return {
        address: this.extractAddressLine(feature),
        city: this.extractCity(context, placeNameParts),
        state: this.extractState(context, placeNameParts),
        zipcode: this.extractZipcode(context, placeNameParts),
        county: this.extractCounty(context),
      };
    },

    extractAddressLine(feature) {
      const streetNumber = feature.address ? String(feature.address).trim() : '';
      const streetName = feature.text ? String(feature.text).trim() : '';

      if (streetNumber && streetName) {
        return `${streetNumber} ${streetName}`.trim();
      }

      if (streetName) {
        return streetName;
      }

      if (typeof feature.place_name === 'string') {
        return feature.place_name.split(',')[0].trim();
      }

      return '';
    },

    extractCity(context, placeNameParts) {
      const placeContext = context.find(item => item.id && item.id.startsWith('place.'));
      const localityContext = context.find(item => item.id && item.id.startsWith('locality.'));
      const districtContext = context.find(item => item.id && item.id.startsWith('district.'));

      return (
          placeContext?.text ||
          localityContext?.text ||
          districtContext?.text ||
          placeNameParts[1] ||
          ''
      );
    },

    extractState(context, placeNameParts) {
      const regionContext = context.find(item => item.id && item.id.startsWith('region.'));

      if (regionContext?.short_code) {
        return this.normalizeStateCode(regionContext.short_code);
      }

      if (regionContext?.text) {
        return this.normalizeStateCode(regionContext.text);
      }

      return this.normalizeStateCode(placeNameParts[2] || '');
    },

    extractZipcode(context, placeNameParts) {
      const postcodeContext = context.find(item => item.id && item.id.startsWith('postcode.'));

      if (postcodeContext?.text) {
        return this.normalizeZipcode(postcodeContext.text);
      }

      const fallback = placeNameParts.find(part => /\d{5}/.test(part)) || '';
      return this.normalizeZipcode(fallback);
    },

    normalizeStateCode(value) {
      if (!value) {
        return '';
      }

      const raw = String(value).trim();

      if (!raw) {
        return '';
      }

      const usDashCodeMatch = raw.match(/^us[- ]([a-z]{2})$/i);
      if (usDashCodeMatch) {
        return usDashCodeMatch[1].toUpperCase();
      }

      const directCodeMatch = raw.match(/^[a-z]{2}$/i);
      if (directCodeMatch) {
        return raw.toUpperCase();
      }

      const stateMap = {
        ALABAMA: 'AL',
        ALASKA: 'AK',
        ARIZONA: 'AZ',
        ARKANSAS: 'AR',
        CALIFORNIA: 'CA',
        COLORADO: 'CO',
        CONNECTICUT: 'CT',
        DELAWARE: 'DE',
        FLORIDA: 'FL',
        GEORGIA: 'GA',
        HAWAII: 'HI',
        IDAHO: 'ID',
        ILLINOIS: 'IL',
        INDIANA: 'IN',
        IOWA: 'IA',
        KANSAS: 'KS',
        KENTUCKY: 'KY',
        LOUISIANA: 'LA',
        MAINE: 'ME',
        MARYLAND: 'MD',
        MASSACHUSETTS: 'MA',
        MICHIGAN: 'MI',
        MINNESOTA: 'MN',
        MISSISSIPPI: 'MS',
        MISSOURI: 'MO',
        MONTANA: 'MT',
        NEBRASKA: 'NE',
        NEVADA: 'NV',
        'NEW HAMPSHIRE': 'NH',
        'NEW JERSEY': 'NJ',
        'NEW MEXICO': 'NM',
        'NEW YORK': 'NY',
        'NORTH CAROLINA': 'NC',
        'NORTH DAKOTA': 'ND',
        OHIO: 'OH',
        OKLAHOMA: 'OK',
        OREGON: 'OR',
        PENNSYLVANIA: 'PA',
        'RHODE ISLAND': 'RI',
        'SOUTH CAROLINA': 'SC',
        'SOUTH DAKOTA': 'SD',
        TENNESSEE: 'TN',
        TEXAS: 'TX',
        UTAH: 'UT',
        VERMONT: 'VT',
        VIRGINIA: 'VA',
        WASHINGTON: 'WA',
        'WEST VIRGINIA': 'WV',
        WISCONSIN: 'WI',
        WYOMING: 'WY',
        'DISTRICT OF COLUMBIA': 'DC',
      };

      return stateMap[raw.toUpperCase()] || raw.substring(0, 2).toUpperCase();
    },

    normalizeZipcode(value) {
      if (!value) {
        return '';
      }

      const digits = String(value).match(/\d/g);

      if (!digits) {
        return '';
      }

      return digits.join('').substring(0, 5);
    },

    onZipBlur() {
      this.$emit('update:zipcode', this.normalizeZipcode(this.zipcode));
    },

    onAddressKeydown(event) {
      if (!this.showAddressSuggestions) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeSuggestionIndex = Math.min(this.activeSuggestionIndex + 1, this.addressSuggestions.length - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeSuggestionIndex = Math.max(this.activeSuggestionIndex - 1, 0);
      } else if (event.key === 'Enter' && this.activeSuggestionIndex >= 0) {
        event.preventDefault();
        this.selectAddressSuggestion(this.addressSuggestions[this.activeSuggestionIndex]);
      } else if (event.key === 'Escape') {
        this.clearAddressSuggestions();
      }
    },

    handleWindowClick(event) {
      if (!this.$refs.addressAutocompleteWrapper) {
        return;
      }

      if (!this.$refs.addressAutocompleteWrapper.contains(event.target)) {
        this.clearAddressSuggestions();
      }
    },

    clearAddressSuggestions() {
      this.addressSuggestions = [];
      this.activeSuggestionIndex = -1;
      this.addressSearchLoading = false;
    },

    updateState(state) {
      this.$emit('update:state', this.normalizeStateCode(state));
    },

    extractCounty(context) {
      const districtContext = context.find(item => item.id && item.id.startsWith('district.'));

      if (districtContext?.text) {
        return districtContext.text;
      }

      return '';
    },

  },
};
</script>

<template>
  <div>
    <div ref="addressAutocompleteWrapper" class="relative">
      <div class="flex flex-row flex-wrap flex-1 items-center my-1">
        <label for="address" class="font-semibold">Address </label>
        <input
            id="address"
            name="address"
            v-model="addressValue"
            :disabled="locked"
            :class="editableClasses"
            type="text"
            autocomplete="street-address"
            @keydown="onAddressKeydown"
        >
      </div>
      <div class="text-red">
        {{ errorFor('address') }}
      </div>

      <div
          v-if="showAddressSuggestions"
          class="absolute z-20 w-full bg-white border border-grey-lighter rounded shadow-lg mt-1 overflow-hidden"
      >
        <button
            v-for="(suggestion, index) in addressSuggestions"
            :key="suggestion.id"
            type="button"
            class="block w-full text-left px-3 py-2 hover:bg-grey-lightest focus:bg-grey-lightest"
            :class="{ 'bg-grey-lightest': index === activeSuggestionIndex }"
            @mousedown.prevent="selectAddressSuggestion(suggestion)"
            @click.prevent="selectAddressSuggestion(suggestion)"
        >
          {{ suggestion.place_name }}
        </button>
      </div>
    </div>

    <div class="flex flex-row flex-wrap flex-1 items-center my-1">
      <label for="city" class="font-semibold">City</label>
      <input
          id="city"
          name="city"
          v-model="cityValue"
          :disabled="locked"
          :class="editableClasses"
          type="text"
          autocomplete="address-level2"
      >
      <div class="text-red">
        {{ errorFor('city') }}
      </div>
    </div>

    <div class="flex flex-row flex-wrap flex-1 items-center my-1">
      <label for="state" class="font-semibold block">State</label>

      <StateSelector
          id="state"
          name="state"
          v-model="stateValue"
          :disabled="locked"
          :class="editableClassesSelect"
          :state-value="state"
          :county-value="county"
          :county-name="pendingCountyName"
          @updateState="updateState"
          @updateCounty="$emit('update:county', $event)"
      />
      <div class="text-red">
        {{ errorFor('state') }}
      </div>
    </div>

    <div class="flex flex-row flex-wrap flex-1 items-center my-1">
      <label for="zipcode" class="font-semibold">Zipcode</label>

      <input
          id="zipcode"
          name="zipcode"
          v-model="zipcodeValue"
          :disabled="locked"
          :class="editableClasses"
          type="text"
          inputmode="numeric"
          autocomplete="postal-code"
          @blur="onZipBlur"
      >
      <div class="text-red">
        {{ errorFor('zipcode') }}
      </div>
    </div>
  </div>
</template>